import React from 'react';
import { X, Edit, Calendar, Clock } from 'lucide-react';
import type { TravelAgency } from '../../../../../types/travelAgencyMaintenance';
import './TravelAgencyDetailsModal.css';

interface TravelAgencyDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  travelAgency: TravelAgency | null;
  onEdit?: (travelAgency: TravelAgency) => void;
}

const TravelAgencyDetailsModal: React.FC<TravelAgencyDetailsModalProps> = ({
  isOpen,
  onClose,
  travelAgency,
  onEdit
}) => {
  if (!isOpen || !travelAgency) {
    return null;
  }

  const handleEdit = () => {
    if (onEdit) {
      onEdit(travelAgency);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="travel-agency-modal-overlay" onClick={onClose}>
      <div className="travel-agency-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="travel-agency-modal-header">
          <div className="travel-agency-modal-header-left">
            <div className="travel-agency-modal-icon">
              <div className={`status-dot ${travelAgency.isActive ? 'active' : 'inactive'}`}></div>
            </div>
            <div className="travel-agency-modal-title-section">
              <h2 className="travel-agency-modal-title">Travel Agency Details</h2>
              <div className="travel-agency-modal-subtitle">
                {travelAgency.taCode} - {travelAgency.taName}
              </div>
            </div>
          </div>
          <div className="travel-agency-modal-header-actions">
            <button
              className="travel-agency-modal-edit-btn"
              onClick={handleEdit}
              title="Edit Travel Agency"
            >
              <Edit size={16} />
              Edit
            </button>
            <button
              className="travel-agency-modal-close-btn"
              onClick={onClose}
              title="Close Modal"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="travel-agency-modal-content">
          <div className="travel-agency-details-grid">
            {/* Basic Information */}
            <div className="travel-agency-detail-section">
              <h3 className="travel-agency-detail-section-title">Basic Information</h3>
              <div className="travel-agency-detail-grid">
                <div className="travel-agency-detail-item">
                  <label className="travel-agency-detail-label">TA Code</label>
                  <div className="travel-agency-detail-value ta-code">{travelAgency.taCode}</div>
                </div>
                <div className="travel-agency-detail-item">
                  <label className="travel-agency-detail-label">Status</label>
                  <div className="travel-agency-detail-value">
                    <div className={`status-indicator ${travelAgency.isActive ? 'active' : 'inactive'}`}>
                      <div className={`status-dot ${travelAgency.isActive ? 'active' : 'inactive'}`}></div>
                      {travelAgency.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
                <div className="travel-agency-detail-item travel-agency-name-item">
                  <label className="travel-agency-detail-label">Travel Agency Name</label>
                  <div className="travel-agency-detail-value">{travelAgency.taName}</div>
                </div>
              </div>
            </div>

            {/* System Information */}
            <div className="travel-agency-detail-section">
              <h3 className="travel-agency-detail-section-title">System Information</h3>
              <div className="travel-agency-detail-grid">
                <div className="travel-agency-detail-item">
                  <label className="travel-agency-detail-label">
                    <Calendar size={14} />
                    Created Date
                  </label>
                  <div className="travel-agency-detail-value">{formatDate(travelAgency.createdAt)}</div>
                </div>
                {travelAgency.lastModified && (
                  <div className="travel-agency-detail-item">
                    <label className="travel-agency-detail-label">
                      <Clock size={14} />
                      Last Modified
                    </label>
                    <div className="travel-agency-detail-value">{formatDate(travelAgency.lastModified)}</div>
                  </div>
                )}
                <div className="travel-agency-detail-item">
                  <label className="travel-agency-detail-label">Record ID</label>
                  <div className="travel-agency-detail-value system-id">{travelAgency.id}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="travel-agency-modal-footer">
          <div className="travel-agency-modal-footer-info">
            <span className="travel-agency-modal-footer-text">
              Travel Agency record for {travelAgency.taCode}
            </span>
          </div>
          <div className="travel-agency-modal-footer-actions">
            <button
              className="travel-agency-modal-btn secondary"
              onClick={onClose}
            >
              Close
            </button>
            <button
              className="travel-agency-modal-btn primary"
              onClick={handleEdit}
            >
              <Edit size={16} />
              Edit Travel Agency
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelAgencyDetailsModal;